import Phaser from "phaser";
import type {
  Direction,
  MonsterQueueItem,
  QueueItem,
  ResolveInput,
  ResolveResult,
} from "@phaser/core/queueItem/queueItem";
import { playMonsterFailEffect } from "@phaser/core/monsters/MonsterFailEffect";

interface QueueItemProvider {
  getNextItem(): QueueItem;
}

interface ItemQueueConfig {
  width: number;
  height: number;
  itemProvider: QueueItemProvider;
  /**
   * 아이템 하나가 큐에서 빠질 때 호출되는 콜백
   * (점수 계산 / 정답 체크는 GameScene에서 담당)
   */
  onItemResolved?: (item: QueueItem, input: ResolveInput) => ResolveResult;
}

// 몬스터 + 장애물 대기열을 담당하는 클래스
export class ItemQueue {
  private scene: Phaser.Scene;
  private width: number;
  private height: number;
  private itemProvider: QueueItemProvider;
  private queueSprites: Phaser.GameObjects.Image[] = [];
  private onItemResolved?: (
    item: QueueItem,
    input: ResolveInput,
  ) => ResolveResult;

  constructor(scene: Phaser.Scene, config: ItemQueueConfig) {
    this.scene = scene;
    this.width = config.width;
    this.height = config.height;
    this.itemProvider = config.itemProvider;
    this.onItemResolved = config.onItemResolved;

    // 초기 3마리 생성 + 배치
    for (let i = 0; i < 3; i++) {
      this.addQueueItem();
    }
    this.layoutItems();
  }

  // 외부에서 호출: 방향 선택(← / → 버튼 클릭)
  public proceed(input: ResolveInput) {
    const first = this.queueSprites.shift();
    if (!first) return;

    const item = first.getData("queueItem") as QueueItem;

    const result = this.onItemResolved?.(item, input) ?? {
      success: true,
      consume: true,
    };

    if (!result.consume) {
      this.queueSprites.unshift(first);
      this.layoutItems();
      return;
    }

    // 뒤에 새 몬스터 추가
    this.addQueueItem();

    // 다시 레이아웃 재정렬 (남아 있는 2마리 + 새로 들어온 1마리)
    this.layoutItems();

    if (item.kind === "MONSTER") {
      this.playMonsterResolveAnimation(first, item, result, input);
      return;
    }

    this.playObstacleResolveAnimation(first, result, input);
  }

  // 새로운 아이템을 큐에 추가
  private addQueueItem() {
    const item = this.itemProvider.getNextItem();

    const sprite = this.scene.add.image(
      this.width / 2,
      this.height * 0.55,
      item.data.textureKey,
    );

    sprite.setOrigin(0.5, 1);
    sprite.setScale(0.05);
    sprite.setData("queueItem", item);

    this.queueSprites.push(sprite);
  }

  // 큐 아이템들의 위치, 크기, 투명도 조정
  private layoutItems() {
    const { height } = this;

    const configs = [
      // 0번: 제일 앞(제일 크게, 가장 아래)
      { depth: 3, scale: 0.72, designY: 538 + 373, alpha: 1 },
      { depth: 2, scale: 0.29, designY: 511 + 154, alpha: 0.8 },
      { depth: 1, scale: 0.22, designY: 477 + 114, alpha: 0.6 },
    ] as const;

    this.queueSprites.forEach((sprite, index) => {
      const cfg = configs[index];
      if (!cfg) return;

      const y = (height * cfg.designY) / 1366;

      sprite.setDepth(cfg.depth);

      this.scene.tweens.add({
        targets: sprite,
        y,
        scale: cfg.scale,
        alpha: cfg.alpha,
        duration: 150,
        ease: "Quad.easeOut",
      });
    });
  }

  private playMonsterResolveAnimation(
    sprite: Phaser.GameObjects.Image,
    item: MonsterQueueItem,
    result: ResolveResult,
    input: ResolveInput,
  ) {
    const direction: Direction = input.type === "RIGHT" ? "RIGHT" : "LEFT";
    const targetX =
      direction === "LEFT" ? this.width * 0.22 : this.width * 0.78;
    const targetY = this.height + 300;
    const rotateAngle = direction === "LEFT" ? -180 : 180;

    this.scene.tweens.add({
      targets: sprite,
      x: targetX,
      y: targetY,
      angle: rotateAngle,
      duration: 500,
      ease: "Cubic.easeIn",
      onComplete: () => {
        sprite.destroy();

        if (!result.success) {
          playMonsterFailEffect(this.scene, item.data, direction, {
            width: this.width,
            height: this.height,
          });
        }
      },
    });
  }

  private playObstacleResolveAnimation(
    sprite: Phaser.GameObjects.Image,
    result: ResolveResult,
    input: ResolveInput,
  ) {
    // 방해물 처리 실패: 떨어지는 연출 없이 바로 사라짐
    if (!result.success) {
      sprite.destroy();
      return;
    }

    const direction: Direction = input.type === "RIGHT" ? "RIGHT" : "LEFT";
    const targetX = direction === "LEFT" ? this.width * 0.1 : this.width * 0.9;

    // 방해물 처리 성공: 위로 날아감
    this.scene.tweens.add({
      targets: sprite,
      x: targetX,
      y: -200,
      angle: direction === "LEFT" ? -20 : 20,
      scale: sprite.scale * 0.8,
      duration: 500,
      ease: "Cubic.easeOut",
      onComplete: () => {
        sprite.destroy();
      },
    });
  }

  // 현재 큐의 가장 앞 아이템 반환
  public getCurrentQueueItem(): QueueItem | null {
    const first = this.queueSprites[0];
    if (!first) return null;

    return first.getData("queueItem") as QueueItem;
  }
}
